import React from 'react';
import { makeStyles } from '@material-ui/core';
import lodashGet from 'lodash/get';
import { useFormatMessage } from 'react-intl-hooks';
import { addCommas } from '../../../utils';
import { COLOR_BLACK, COLOR_WHITE } from '../../colors';
import { skins } from '../skins';
import { HistoricalTrend } from '../HistoricalTrend';

const COPYRIGHT_YEAR = new Date().getFullYear();
const useStyles = makeStyles((theme)=> ({
    highlightContainer: {
        fontSize: '72px',
        lineHeight: '82px',
        fontWeight: 700,
        display: 'inline-block',
        padding: '6px 16px 6px 16px',
        position: 'relative',
        color: 'white'
    },
    highlight: {
        width: '100%',
        height: '76px',
        position: 'absolute',
        transform: 'matrix(1, -0.02, 0.02, 1, 0, 0)',
        bottom: '10px',
        left: 0,
        zIndex: 1,
    },
    highlightValue: {
        position: 'relative',
        zIndex: 2,
    },
    columnWraper: {
        display: 'flex',
        height: '372px',
    },
    columnItem1: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        marginTop: '36px',
    },
    columnItem1Month: {
        fontSize: '24px',
        lineHeight: '32px',
    },
    columnItem1Area: {
        fontSize: '34px',
        lineHeight: '48px',
        fontWeight: 900,
        margin: '8px auto 0 auto',
        maxWidth: '347px',
    },
    columnItem1Value: {
        fontSize: '72px',
        lineHeight: '82px',
        fontWeight: 700,
        textAlign: 'center',
        marginTop: '32px',
    },
    columnItem1Label: {
        fontSize: '32px',
        lineHeight: '48px',
        fontWeight: 500,
        textAlign: 'center',
        marginTop: '6px',
    },
    columnItem2: {
        flex: 1,
        marginTop: '26px',
    },
    statsRow: {
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        borderBottom: '2px solid',
        padding: '25px 0 25px 0',
        marginLeft: '32px',
        minHeight: '108px',
        '&:first-child': {
            paddingTop: '0px',
            minHeight: 0,
        },
        '&:nth-last-child(1)': {
            borderBottom: 'none',
            paddingBottom: 0,
        },
    },
    statsRowItem: {
        display: 'flex',
        alignItems: 'center',
        textAlign: 'left',
    },
    statsRowItemValue: {
        fontSize: '60px',
        lineHeight: '84px',
        fontWeight: 700,
        marginRight: '16px',
    },
    statsRowItemLabel: {
        fontSize: '24px',
        lineHeight: '32px',
        fontWeight: 500,
        maxWidth: '180px',
    },
    compliance: {
        position: 'absolute',
        bottom: 16,
        width: '92%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '14px',
        lineHeight: '20px',
        marginTop: '24px',
    },
    disclaimer: {
        display: 'flex',
        alignItems: 'center',
        textAlign: 'end',
    },
    disclaimerText: {
        width: '295px',
        marginRight: '16px',
    },
    copyright: {
        width: '320px',
        textAlign: 'start',
    }
}));

export default function StatsColumns(props) {
    const {
        medianDom: median_dom,
        medianDomTrend: median_dom_mm,
        medianPrice: median_price,
        medianPriceTrend: median_price_mm,
        activeListingsCount: active_listings_count,
        activeListingsCountTrend: active_listings_count_mm,
        newListingsCount: new_listings_count,
        fromMonth,
        toMonth,
        skin,
        month,
        area,
    } = props || {};
    const classes = useStyles(props);
    const t = useFormatMessage();
    const borderBottomColor = lodashGet(skins, `${skin}.LINE_COLOR`, COLOR_WHITE);

    return (
        <>
            <div className={classes.columnWraper}>
                <div className={classes.columnItem1}>
                    <div>
                        <div className={classes.columnItem1Month}>{t({ id: 'title' })} {t({ id: month })}</div>
                        <div className={classes.columnItem1Area}>{area}</div>
                    </div>
                    <div>
                        <div className={classes.columnItem1Value}>
                            <div className={classes.highlightContainer}>
                                <div className={classes.highlight} style={{  backgroundColor: lodashGet(skins, `${skin}.HIGHLIGHT_COLOR`, COLOR_BLACK)}} />
                                <div className={classes.highlightValue}>
                                    {median_price}
                                </div>
                            </div>
                        </div>
                        <div className={classes.columnItem1Label} dangerouslySetInnerHTML={{__html: t({ id: 'median_price' }, { separator: ' ' })}} />
                        <HistoricalTrend
                            value={median_price_mm}
                            fromMonth={fromMonth}
                            toMonth={toMonth}
                            size="large"
                            margin="12px auto"
                        />
                    </div>
                </div>

                <div className={classes.columnItem2}>
                    <div className={classes.statsRow} style={{borderBottomColor}}>
                        <div className={classes.statsRowItem}>
                            <div className={classes.statsRowItemValue}>
                                {`${active_listings_count > 0 ? addCommas(active_listings_count) : '-'}`}
                            </div>
                            <div className={classes.statsRowItemLabel} dangerouslySetInnerHTML={{__html: t({ id: 'active_listings' }, { separator: '<br/>' })}}></div>

                            <HistoricalTrend
                                value={active_listings_count_mm}
                                fromMonth={fromMonth}
                                toMonth={toMonth}
                                size="small"
                                margin="12px auto"
                            />
                        </div>

                    </div>

                    <div className={classes.statsRow} style={{borderBottomColor}}>
                        <div className={classes.statsRowItem}>
                            <div className={classes.statsRowItemValue}>
                                {`${median_dom > 0 ? addCommas(median_dom) : '-'}`}
                            </div>
                            <div className={classes.statsRowItemLabel} dangerouslySetInnerHTML={{__html: t({ id: 'median_dom' }, { separator: '<br/>' })}}></div>

                            <HistoricalTrend
                                value={median_dom_mm}
                                fromMonth={fromMonth}
                                toMonth={toMonth}
                                size="small"
                                margin="12px auto"
                            />
                        </div>
                    </div>

                    <div className={classes.statsRow} style={{borderBottomColor}}>
                        <div className={classes.statsRowItem}>
                            <div className={classes.statsRowItemValue}>
                                {`${new_listings_count > 0 ? addCommas(new_listings_count) : '-'}`}
                            </div>
                            <div className={classes.statsRowItemLabel} dangerouslySetInnerHTML={{__html: t({ id: 'new_listings' }, { separator: ' ' })}}></div>
                        </div>
                    </div>

                </div>
            </div>

            <div className={classes.compliance} style={{ color: lodashGet(skins, `${skin}.COMPLIANCE_COLOR`) }}>
                <div className={classes.copyright}>
                    <img src={lodashGet(skins, `${skin}.LR_LOGO_PATH`)} height={16} alt="LR Logo" />
                    <div>
                        &copy; ListReports {COPYRIGHT_YEAR}, {t({ id: 'copyright' })}
                    </div>
                </div>
                <div className={classes.disclaimer}>
                    <div className={classes.disclaimerText}>{t({ id: 'disclaimer' })}</div>
                    <img src={lodashGet(skins, `${skin}.EQUAL_HOUSING_LOGO_PATH`)} height={24} alt="Equal Housing logo" />
                </div>
            </div>
        </>
    );
}
